import {
  prepareEvent,
  prepareContractCall,
  readContract,
  type BaseTransactionOptions,
  type AbiParameterToPrimitiveType,
} from 'thirdweb'

/**
 * Contract events
 */

/**
 * Represents the filters for the "CanceledOffer" event.
 */
export type CanceledOfferEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the CanceledOffer event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { canceledOfferEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  canceledOfferEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function canceledOfferEvent(filters: CanceledOfferEventFilters = {}) {
  return prepareEvent({
    signature:
      'event CanceledOffer(uint256 indexed offerId, uint256 depositAssetAmountMakerReceived)',
    filters,
  })
}

/**
 * Represents the filters for the "CreatedOffer" event.
 */
export type CreatedOfferEventFilters = Partial<{
  maker: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'maker'
    type: 'address'
  }>
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the CreatedOffer event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { createdOfferEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  createdOfferEvent({
 *  maker: ...,
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function createdOfferEvent(filters: CreatedOfferEventFilters = {}) {
  return prepareEvent({
    signature:
      'event CreatedOffer(address indexed maker, uint256 indexed offerId, (address maker, uint8 offerFillType, (uint8 assetType, address assetAddress, uint256 amount, uint256 tokenId, (address priceFeedAddress, uint256 offerMaximumPrice, uint256 offerMinimumPrice) assetPrice) depositAsset, (uint8 assetType, address assetAddress, uint256 amount, uint256 tokenId, (address priceFeedAddress, uint256 offerMaximumPrice, uint256 offerMinimumPrice) assetPrice) withdrawalAsset, (uint8 takingOfferType, (uint8 offerPricingType, uint256 unitPrice, uint256 percentage, uint8 percentageType) offerPrice, address[] specialAddresses, address[] authorizationAddresses, uint256 expiryTimestamp, uint256 timelockPeriod, string terms, string commsLink) offer) dotcOffer)',
    filters,
  })
}

/**
 * Creates an event object for the Initialized event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { initializedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  initializedEvent()
 * ],
 * });
 * ```
 */
export function initializedEvent() {
  return prepareEvent({
    signature: 'event Initialized(uint64 version)',
  })
}

/**
 * Represents the filters for the "OfferAuthAddressesUpdated" event.
 */
export type OfferAuthAddressesUpdatedEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the OfferAuthAddressesUpdated event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { offerAuthAddressesUpdatedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  offerAuthAddressesUpdatedEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function offerAuthAddressesUpdatedEvent(
  filters: OfferAuthAddressesUpdatedEventFilters = {}
) {
  return prepareEvent({
    signature:
      'event OfferAuthAddressesUpdated(uint256 indexed offerId, address[] authAddresses)',
    filters,
  })
}

/**
 * Represents the filters for the "OfferLinksUpdated" event.
 */
export type OfferLinksUpdatedEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the OfferLinksUpdated event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { offerLinksUpdatedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  offerLinksUpdatedEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function offerLinksUpdatedEvent(
  filters: OfferLinksUpdatedEventFilters = {}
) {
  return prepareEvent({
    signature:
      'event OfferLinksUpdated(uint256 indexed offerId, string newTerms, string newCommsLink)',
    filters,
  })
}

/**
 * Represents the filters for the "OfferSpecialAddressesUpdated" event.
 */
export type OfferSpecialAddressesUpdatedEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the OfferSpecialAddressesUpdated event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { offerSpecialAddressesUpdatedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  offerSpecialAddressesUpdatedEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function offerSpecialAddressesUpdatedEvent(
  filters: OfferSpecialAddressesUpdatedEventFilters = {}
) {
  return prepareEvent({
    signature:
      'event OfferSpecialAddressesUpdated(uint256 indexed offerId, address[] specialAddresses)',
    filters,
  })
}

/**
 * Represents the filters for the "TakenOffer" event.
 */
export type TakenOfferEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
  taker: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'address'
    name: 'taker'
    type: 'address'
  }>
  offerFillType: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'enum OfferFillType'
    name: 'offerFillType'
    type: 'uint8'
  }>
}>

/**
 * Creates an event object for the TakenOffer event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { takenOfferEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  takenOfferEvent({
 *  offerId: ...,
 *  taker: ...,
 *  offerFillType: ...,
 * })
 * ],
 * });
 * ```
 */
export function takenOfferEvent(filters: TakenOfferEventFilters = {}) {
  return prepareEvent({
    signature:
      'event TakenOffer(uint256 indexed offerId, address indexed taker, uint8 indexed offerFillType, uint256 depositAssetAmount, uint256 withdrawalAssetAmount, address affiliate)',
    filters,
  })
}

/**
 * Represents the filters for the "UpdatedOfferExpiry" event.
 */
export type UpdatedOfferExpiryEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the UpdatedOfferExpiry event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { updatedOfferExpiryEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  updatedOfferExpiryEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function updatedOfferExpiryEvent(
  filters: UpdatedOfferExpiryEventFilters = {}
) {
  return prepareEvent({
    signature:
      'event UpdatedOfferExpiry(uint256 indexed offerId, uint256 newExpiryTimestamp)',
    filters,
  })
}

/**
 * Represents the filters for the "UpdatedTimeLockPeriod" event.
 */
export type UpdatedTimeLockPeriodEventFilters = Partial<{
  offerId: AbiParameterToPrimitiveType<{
    indexed: true
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}>

/**
 * Creates an event object for the UpdatedTimeLockPeriod event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { updatedTimeLockPeriodEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  updatedTimeLockPeriodEvent({
 *  offerId: ...,
 * })
 * ],
 * });
 * ```
 */
export function updatedTimeLockPeriodEvent(
  filters: UpdatedTimeLockPeriodEventFilters = {}
) {
  return prepareEvent({
    signature:
      'event UpdatedTimeLockPeriod(uint256 indexed offerId, uint256 newTimelockPeriod)',
    filters,
  })
}

/**
 * Contract read functions
 */

/**
 * Represents the parameters for the "allOffers" function.
 */
export type AllOffersParams = {
  arg_0: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: ''
    type: 'uint256'
  }>
}

/**
 * Calls the "allOffers" function on the contract.
 * @param options - The options for the allOffers function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { allOffers } from "TODO";
 *
 * const result = await allOffers({
 *  arg_0: ...,
 * });
 *
 * ```
 */
export async function allOffers(
  options: BaseTransactionOptions<AllOffersParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x06272a0b',
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'address',
          name: 'maker',
          type: 'address',
        },
        {
          internalType: 'enum OfferFillType',
          name: 'offerFillType',
          type: 'uint8',
        },
        {
          components: [
            {
              internalType: 'enum AssetType',
              name: 'assetType',
              type: 'uint8',
            },
            {
              internalType: 'address',
              name: 'assetAddress',
              type: 'address',
            },
            {
              internalType: 'uint256',
              name: 'amount',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'tokenId',
              type: 'uint256',
            },
            {
              components: [
                {
                  internalType: 'address',
                  name: 'priceFeedAddress',
                  type: 'address',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMaximumPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMinimumPrice',
                  type: 'uint256',
                },
              ],
              internalType: 'struct AssetPrice',
              name: 'assetPrice',
              type: 'tuple',
            },
          ],
          internalType: 'struct Asset',
          name: 'depositAsset',
          type: 'tuple',
        },
        {
          components: [
            {
              internalType: 'enum AssetType',
              name: 'assetType',
              type: 'uint8',
            },
            {
              internalType: 'address',
              name: 'assetAddress',
              type: 'address',
            },
            {
              internalType: 'uint256',
              name: 'amount',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'tokenId',
              type: 'uint256',
            },
            {
              components: [
                {
                  internalType: 'address',
                  name: 'priceFeedAddress',
                  type: 'address',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMaximumPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMinimumPrice',
                  type: 'uint256',
                },
              ],
              internalType: 'struct AssetPrice',
              name: 'assetPrice',
              type: 'tuple',
            },
          ],
          internalType: 'struct Asset',
          name: 'withdrawalAsset',
          type: 'tuple',
        },
        {
          components: [
            {
              internalType: 'enum TakingOfferType',
              name: 'takingOfferType',
              type: 'uint8',
            },
            {
              components: [
                {
                  internalType: 'enum OfferPricingType',
                  name: 'offerPricingType',
                  type: 'uint8',
                },
                {
                  internalType: 'uint256',
                  name: 'unitPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'percentage',
                  type: 'uint256',
                },
                {
                  internalType: 'enum PercentageType',
                  name: 'percentageType',
                  type: 'uint8',
                },
              ],
              internalType: 'struct OfferPrice',
              name: 'offerPrice',
              type: 'tuple',
            },
            {
              internalType: 'address[]',
              name: 'specialAddresses',
              type: 'address[]',
            },
            {
              internalType: 'address[]',
              name: 'authorizationAddresses',
              type: 'address[]',
            },
            {
              internalType: 'uint256',
              name: 'expiryTimestamp',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'timelockPeriod',
              type: 'uint256',
            },
            {
              internalType: 'string',
              name: 'terms',
              type: 'string',
            },
            {
              internalType: 'string',
              name: 'commsLink',
              type: 'string',
            },
          ],
          internalType: 'struct OfferStruct',
          name: 'offer',
          type: 'tuple',
        },
      ],
    ],
    params: [options.arg_0],
  })
}

/**
 * Calls the "currentOfferId" function on the contract.
 * @param options - The options for the currentOfferId function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { currentOfferId } from "TODO";
 *
 * const result = await currentOfferId();
 *
 * ```
 */
export async function currentOfferId(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0xcbd039ed',
      [],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Calls the "escrow" function on the contract.
 * @param options - The options for the escrow function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { escrow } from "TODO";
 *
 * const result = await escrow();
 *
 * ```
 */
export async function escrow(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0xe2fdcc17',
      [],
      [
        {
          internalType: 'contract DotcEscrowV2',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Calls the "manager" function on the contract.
 * @param options - The options for the manager function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { manager } from "TODO";
 *
 * const result = await manager();
 *
 * ```
 */
export async function manager(options: BaseTransactionOptions) {
  return readContract({
    contract: options.contract,
    method: [
      '0x481c6a75',
      [],
      [
        {
          internalType: 'contract DotcManagerV2',
          name: '',
          type: 'address',
        },
      ],
    ],
    params: [],
  })
}

/**
 * Represents the parameters for the "offersFromAddress" function.
 */
export type OffersFromAddressParams = {
  arg_0: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: ''
    type: 'address'
  }>
  arg_1: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: ''
    type: 'uint256'
  }>
}

/**
 * Calls the "offersFromAddress" function on the contract.
 * @param options - The options for the offersFromAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { offersFromAddress } from "TODO";
 *
 * const result = await offersFromAddress({
 *  arg_0: ...,
 *  arg_1: ...,
 * });
 *
 * ```
 */
export async function offersFromAddress(
  options: BaseTransactionOptions<OffersFromAddressParams>
) {
  return readContract({
    contract: options.contract,
    method: [
      '0x10b6b623',
      [
        {
          internalType: 'address',
          name: '',
          type: 'address',
        },
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
      [
        {
          internalType: 'uint256',
          name: '',
          type: 'uint256',
        },
      ],
    ],
    params: [options.arg_0, options.arg_1],
  })
}

/**
 * Contract write functions
 */

/**
 * Represents the parameters for the "cancelOffer" function.
 */
export type CancelOfferParams = {
  offerId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
}

/**
 * Calls the "cancelOffer" function on the contract.
 * @param options - The options for the "cancelOffer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { cancelOffer } from "TODO";
 *
 * const transaction = cancelOffer({
 *  offerId: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function cancelOffer(
  options: BaseTransactionOptions<CancelOfferParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xef706adf',
      [
        {
          internalType: 'uint256',
          name: 'offerId',
          type: 'uint256',
        },
      ],
      [],
    ],
    params: [options.offerId],
  })
}

/**
 * Represents the parameters for the "changeEscrow" function.
 */
export type ChangeEscrowParams = {
  escrow: AbiParameterToPrimitiveType<{
    internalType: 'contract DotcEscrowV2'
    name: '_escrow'
    type: 'address'
  }>
}

/**
 * Calls the "changeEscrow" function on the contract.
 * @param options - The options for the "changeEscrow" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { changeEscrow } from "TODO";
 *
 * const transaction = changeEscrow({
 *  escrow: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function changeEscrow(
  options: BaseTransactionOptions<ChangeEscrowParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xdcfcda2b',
      [
        {
          internalType: 'contract DotcEscrowV2',
          name: '_escrow',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.escrow],
  })
}

/**
 * Represents the parameters for the "initialize" function.
 */
export type InitializeParams = {
  manager: AbiParameterToPrimitiveType<{
    internalType: 'contract DotcManagerV2'
    name: '_manager'
    type: 'address'
  }>
}

/**
 * Calls the "initialize" function on the contract.
 * @param options - The options for the "initialize" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { initialize } from "TODO";
 *
 * const transaction = initialize({
 *  manager: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function initialize(options: BaseTransactionOptions<InitializeParams>) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xc4d66de8',
      [
        {
          internalType: 'contract DotcManagerV2',
          name: '_manager',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.manager],
  })
}

/**
 * Represents the parameters for the "makeOffer" function.
 */
export type MakeOfferParams = {
  depositAsset: AbiParameterToPrimitiveType<{
    components: [
      { internalType: 'enum AssetType'; name: 'assetType'; type: 'uint8' },
      { internalType: 'address'; name: 'assetAddress'; type: 'address' },
      { internalType: 'uint256'; name: 'amount'; type: 'uint256' },
      { internalType: 'uint256'; name: 'tokenId'; type: 'uint256' },
      {
        components: [
          {
            internalType: 'address'
            name: 'priceFeedAddress'
            type: 'address'
          },
          {
            internalType: 'uint256'
            name: 'offerMaximumPrice'
            type: 'uint256'
          },
          {
            internalType: 'uint256'
            name: 'offerMinimumPrice'
            type: 'uint256'
          }
        ]
        internalType: 'struct AssetPrice'
        name: 'assetPrice'
        type: 'tuple'
      }
    ]
    internalType: 'struct Asset'
    name: 'depositAsset'
    type: 'tuple'
  }>
  withdrawalAsset: AbiParameterToPrimitiveType<{
    components: [
      { internalType: 'enum AssetType'; name: 'assetType'; type: 'uint8' },
      { internalType: 'address'; name: 'assetAddress'; type: 'address' },
      { internalType: 'uint256'; name: 'amount'; type: 'uint256' },
      { internalType: 'uint256'; name: 'tokenId'; type: 'uint256' },
      {
        components: [
          {
            internalType: 'address'
            name: 'priceFeedAddress'
            type: 'address'
          },
          {
            internalType: 'uint256'
            name: 'offerMaximumPrice'
            type: 'uint256'
          },
          {
            internalType: 'uint256'
            name: 'offerMinimumPrice'
            type: 'uint256'
          }
        ]
        internalType: 'struct AssetPrice'
        name: 'assetPrice'
        type: 'tuple'
      }
    ]
    internalType: 'struct Asset'
    name: 'withdrawalAsset'
    type: 'tuple'
  }>
  offer: AbiParameterToPrimitiveType<{
    components: [
      {
        internalType: 'enum TakingOfferType'
        name: 'takingOfferType'
        type: 'uint8'
      },
      {
        components: [
          {
            internalType: 'enum OfferPricingType'
            name: 'offerPricingType'
            type: 'uint8'
          },
          { internalType: 'uint256'; name: 'unitPrice'; type: 'uint256' },
          { internalType: 'uint256'; name: 'percentage'; type: 'uint256' },
          {
            internalType: 'enum PercentageType'
            name: 'percentageType'
            type: 'uint8'
          }
        ]
        internalType: 'struct OfferPrice'
        name: 'offerPrice'
        type: 'tuple'
      },
      {
        internalType: 'address[]'
        name: 'specialAddresses'
        type: 'address[]'
      },
      {
        internalType: 'address[]'
        name: 'authorizationAddresses'
        type: 'address[]'
      },
      { internalType: 'uint256'; name: 'expiryTimestamp'; type: 'uint256' },
      { internalType: 'uint256'; name: 'timelockPeriod'; type: 'uint256' },
      { internalType: 'string'; name: 'terms'; type: 'string' },
      { internalType: 'string'; name: 'commsLink'; type: 'string' }
    ]
    internalType: 'struct OfferStruct'
    name: 'offer'
    type: 'tuple'
  }>
}

/**
 * Calls the "makeOffer" function on the contract.
 * @param options - The options for the "makeOffer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { makeOffer } from "TODO";
 *
 * const transaction = makeOffer({
 *  depositAsset: ...,
 *  withdrawalAsset: ...,
 *  offer: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function makeOffer(options: BaseTransactionOptions<MakeOfferParams>) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xfd8b999c',
      [
        {
          components: [
            {
              internalType: 'enum AssetType',
              name: 'assetType',
              type: 'uint8',
            },
            {
              internalType: 'address',
              name: 'assetAddress',
              type: 'address',
            },
            {
              internalType: 'uint256',
              name: 'amount',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'tokenId',
              type: 'uint256',
            },
            {
              components: [
                {
                  internalType: 'address',
                  name: 'priceFeedAddress',
                  type: 'address',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMaximumPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMinimumPrice',
                  type: 'uint256',
                },
              ],
              internalType: 'struct AssetPrice',
              name: 'assetPrice',
              type: 'tuple',
            },
          ],
          internalType: 'struct Asset',
          name: 'depositAsset',
          type: 'tuple',
        },
        {
          components: [
            {
              internalType: 'enum AssetType',
              name: 'assetType',
              type: 'uint8',
            },
            {
              internalType: 'address',
              name: 'assetAddress',
              type: 'address',
            },
            {
              internalType: 'uint256',
              name: 'amount',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'tokenId',
              type: 'uint256',
            },
            {
              components: [
                {
                  internalType: 'address',
                  name: 'priceFeedAddress',
                  type: 'address',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMaximumPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'offerMinimumPrice',
                  type: 'uint256',
                },
              ],
              internalType: 'struct AssetPrice',
              name: 'assetPrice',
              type: 'tuple',
            },
          ],
          internalType: 'struct Asset',
          name: 'withdrawalAsset',
          type: 'tuple',
        },
        {
          components: [
            {
              internalType: 'enum TakingOfferType',
              name: 'takingOfferType',
              type: 'uint8',
            },
            {
              components: [
                {
                  internalType: 'enum OfferPricingType',
                  name: 'offerPricingType',
                  type: 'uint8',
                },
                {
                  internalType: 'uint256',
                  name: 'unitPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'percentage',
                  type: 'uint256',
                },
                {
                  internalType: 'enum PercentageType',
                  name: 'percentageType',
                  type: 'uint8',
                },
              ],
              internalType: 'struct OfferPrice',
              name: 'offerPrice',
              type: 'tuple',
            },
            {
              internalType: 'address[]',
              name: 'specialAddresses',
              type: 'address[]',
            },
            {
              internalType: 'address[]',
              name: 'authorizationAddresses',
              type: 'address[]',
            },
            {
              internalType: 'uint256',
              name: 'expiryTimestamp',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'timelockPeriod',
              type: 'uint256',
            },
            {
              internalType: 'string',
              name: 'terms',
              type: 'string',
            },
            {
              internalType: 'string',
              name: 'commsLink',
              type: 'string',
            },
          ],
          internalType: 'struct OfferStruct',
          name: 'offer',
          type: 'tuple',
        },
      ],
      [],
    ],
    params: [options.depositAsset, options.withdrawalAsset, options.offer],
  })
}

/**
 * Represents the parameters for the "takeOfferDynamic" function.
 */
export type TakeOfferDynamicParams = {
  offerId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
  withdrawalAmountPaid: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'withdrawalAmountPaid'
    type: 'uint256'
  }>
  maximumDepositToWithdrawalRate: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'maximumDepositToWithdrawalRate'
    type: 'uint256'
  }>
  affiliate: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'affiliate'
    type: 'address'
  }>
}

/**
 * Calls the "takeOfferDynamic" function on the contract.
 * @param options - The options for the "takeOfferDynamic" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { takeOfferDynamic } from "TODO";
 *
 * const transaction = takeOfferDynamic({
 *  offerId: ...,
 *  withdrawalAmountPaid: ...,
 *  maximumDepositToWithdrawalRate: ...,
 *  affiliate: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function takeOfferDynamic(
  options: BaseTransactionOptions<TakeOfferDynamicParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x76db472f',
      [
        {
          internalType: 'uint256',
          name: 'offerId',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'withdrawalAmountPaid',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'maximumDepositToWithdrawalRate',
          type: 'uint256',
        },
        {
          internalType: 'address',
          name: 'affiliate',
          type: 'address',
        },
      ],
      [],
    ],
    params: [
      options.offerId,
      options.withdrawalAmountPaid,
      options.maximumDepositToWithdrawalRate,
      options.affiliate,
    ],
  })
}

/**
 * Represents the parameters for the "takeOfferFixed" function.
 */
export type TakeOfferFixedParams = {
  offerId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
  withdrawalAmountPaid: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'withdrawalAmountPaid'
    type: 'uint256'
  }>
  affiliate: AbiParameterToPrimitiveType<{
    internalType: 'address'
    name: 'affiliate'
    type: 'address'
  }>
}

/**
 * Calls the "takeOfferFixed" function on the contract.
 * @param options - The options for the "takeOfferFixed" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { takeOfferFixed } from "TODO";
 *
 * const transaction = takeOfferFixed({
 *  offerId: ...,
 *  withdrawalAmountPaid: ...,
 *  affiliate: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function takeOfferFixed(
  options: BaseTransactionOptions<TakeOfferFixedParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0xec966683',
      [
        {
          internalType: 'uint256',
          name: 'offerId',
          type: 'uint256',
        },
        {
          internalType: 'uint256',
          name: 'withdrawalAmountPaid',
          type: 'uint256',
        },
        {
          internalType: 'address',
          name: 'affiliate',
          type: 'address',
        },
      ],
      [],
    ],
    params: [options.offerId, options.withdrawalAmountPaid, options.affiliate],
  })
}

/**
 * Represents the parameters for the "updateOffer" function.
 */
export type UpdateOfferParams = {
  offerId: AbiParameterToPrimitiveType<{
    internalType: 'uint256'
    name: 'offerId'
    type: 'uint256'
  }>
  updatedOffer: AbiParameterToPrimitiveType<{
    components: [
      {
        internalType: 'enum TakingOfferType'
        name: 'takingOfferType'
        type: 'uint8'
      },
      {
        components: [
          {
            internalType: 'enum OfferPricingType'
            name: 'offerPricingType'
            type: 'uint8'
          },
          { internalType: 'uint256'; name: 'unitPrice'; type: 'uint256' },
          { internalType: 'uint256'; name: 'percentage'; type: 'uint256' },
          {
            internalType: 'enum PercentageType'
            name: 'percentageType'
            type: 'uint8'
          }
        ]
        internalType: 'struct OfferPrice'
        name: 'offerPrice'
        type: 'tuple'
      },
      {
        internalType: 'address[]'
        name: 'specialAddresses'
        type: 'address[]'
      },
      {
        internalType: 'address[]'
        name: 'authorizationAddresses'
        type: 'address[]'
      },
      { internalType: 'uint256'; name: 'expiryTimestamp'; type: 'uint256' },
      { internalType: 'uint256'; name: 'timelockPeriod'; type: 'uint256' },
      { internalType: 'string'; name: 'terms'; type: 'string' },
      { internalType: 'string'; name: 'commsLink'; type: 'string' }
    ]
    internalType: 'struct OfferStruct'
    name: 'updatedOffer'
    type: 'tuple'
  }>
}

/**
 * Calls the "updateOffer" function on the contract.
 * @param options - The options for the "updateOffer" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { updateOffer } from "TODO";
 *
 * const transaction = updateOffer({
 *  offerId: ...,
 *  updatedOffer: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function updateOffer(
  options: BaseTransactionOptions<UpdateOfferParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
      '0x1fd0063b',
      [
        {
          internalType: 'uint256',
          name: 'offerId',
          type: 'uint256',
        },
        {
          components: [
            {
              internalType: 'enum TakingOfferType',
              name: 'takingOfferType',
              type: 'uint8',
            },
            {
              components: [
                {
                  internalType: 'enum OfferPricingType',
                  name: 'offerPricingType',
                  type: 'uint8',
                },
                {
                  internalType: 'uint256',
                  name: 'unitPrice',
                  type: 'uint256',
                },
                {
                  internalType: 'uint256',
                  name: 'percentage',
                  type: 'uint256',
                },
                {
                  internalType: 'enum PercentageType',
                  name: 'percentageType',
                  type: 'uint8',
                },
              ],
              internalType: 'struct OfferPrice',
              name: 'offerPrice',
              type: 'tuple',
            },
            {
              internalType: 'address[]',
              name: 'specialAddresses',
              type: 'address[]',
            },
            {
              internalType: 'address[]',
              name: 'authorizationAddresses',
              type: 'address[]',
            },
            {
              internalType: 'uint256',
              name: 'expiryTimestamp',
              type: 'uint256',
            },
            {
              internalType: 'uint256',
              name: 'timelockPeriod',
              type: 'uint256',
            },
            {
              internalType: 'string',
              name: 'terms',
              type: 'string',
            },
            {
              internalType: 'string',
              name: 'commsLink',
              type: 'string',
            },
          ],
          internalType: 'struct OfferStruct',
          name: 'updatedOffer',
          type: 'tuple',
        },
      ],
      [],
    ],
    params: [options.offerId, options.updatedOffer],
  })
}
